import { useState, useEffect, useCallback, useRef } from 'react'
import { message } from 'antd'
import {
  editVideo,
  getTaskStatus
} from '../../../../../services/api/aiVideoGeneration'

const POLL_INTERVAL = 15000 // 15 seconds

export const useVideoEditor = ({
  persistedConversation = [],
  activeTaskId = null,
  initialReference = null,
  onUpdateState = () => {},
  videoId
}) => {
  const [conversation, setConversation] = useState(persistedConversation)
  const [isGenerating, setIsGenerating] = useState(!!activeTaskId)
  const [taskId, setTaskId] = useState(activeTaskId)
  const [selectedReference, setSelectedReference] = useState(initialReference)

  const pollingRef = useRef(false)
  const mountedRef = useRef(true)
  const initializedRef = useRef(false)
  const onUpdateStateRef = useRef(onUpdateState)
  const conversationRef = useRef(conversation)
  const lastFailedTaskIdRef = useRef(null)

  useEffect(() => {
    conversationRef.current = conversation
  }, [conversation])

  useEffect(() => {
    onUpdateStateRef.current = onUpdateState
  }, [onUpdateState])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Update internal state when persisted props change (if modal is re-opened)
  useEffect(() => {
    // Only initialize from props once or if local state is empty
    if (
      !initializedRef.current &&
      (persistedConversation.length > 0 || conversation.length > 0)
    ) {
      if (conversation.length === 0 && persistedConversation.length > 0) {
        setConversation(persistedConversation)
      }
      initializedRef.current = true
    }

    if (
      activeTaskId &&
      activeTaskId !== taskId &&
      activeTaskId !== lastFailedTaskIdRef.current
    ) {
      setTaskId(activeTaskId)
      setIsGenerating(true)
    }

    if (initialReference && !selectedReference) {
      setSelectedReference(initialReference)
    }
  }, [
    persistedConversation,
    activeTaskId,
    initialReference,
    taskId,
    selectedReference,
    conversation.length
  ])

  const pollTask = useCallback(async currentTaskId => {
    if (pollingRef.current) return
    pollingRef.current = true
    setIsGenerating(true)
    console.log(`[VideoEditor] Starting polling for task: ${currentTaskId}`)

    const loadingId = Date.now()
    // Add loading indicator if not already there
    setConversation(prev => {
      if (prev.some(m => m.type === 'loading')) return prev
      return [...prev, { type: 'loading', id: loadingId }]
    })

    try {
      while (mountedRef.current) {
        console.log(`[VideoEditor] Checking status for task: ${currentTaskId}`)
        const res = await getTaskStatus(currentTaskId)
        const status = res?.data?.status
        const data = res?.data || {}

        console.log(`[VideoEditor] Task ${currentTaskId} status: ${status}`)

        if (status === 'success') {
          const resultUrl =
            typeof data.output === 'string'
              ? data.output
              : data.output?.video_url || data.output?.url

          if (resultUrl) {
            const nextConv = conversationRef.current
              .filter(msg => msg.type !== 'loading')
              .concat({
                type: 'assistant',
                content: resultUrl,
                timestamp: Date.now()
              })

            setConversation(nextConv)
            setSelectedReference(resultUrl) // Auto-select new video as reference
            setTaskId(null)
            onUpdateStateRef.current(nextConv, null)
            break
          }
        }

        if (status === 'failed' || status === 'error') {
          const errorMsg = data?.error_msg || data?.error || 'Task failed'
          console.error(`[VideoEditor] Task ${currentTaskId} failed:`, errorMsg)

          const nextConv = conversationRef.current
            .filter(msg => msg.type !== 'loading')
            .concat({
              type: 'error',
              content: errorMsg,
              timestamp: Date.now()
            })

          lastFailedTaskIdRef.current = currentTaskId // Prevent immediate restart
          setConversation(nextConv)
          setTaskId(null)
          onUpdateStateRef.current(nextConv, null)
          break
        }

        // Report progress if available
        const progress = data?.progress
        if (progress !== undefined) {
          const progressVal = parseInt(progress) || 0
          onUpdateStateRef.current(
            conversationRef.current,
            currentTaskId,
            progressVal
          )
        }

        // If progress is 100%, retry quickly
        if (progress === '100%' || progress === 100) {
          await new Promise(resolve => setTimeout(resolve, 2000))
          continue
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }
    } catch (err) {
      console.error('[VideoEditor] Polling error', err)
      setConversation(prev => {
        const newConv = prev
          .filter(msg => msg.type !== 'loading')
          .concat({
            type: 'error',
            content: err.message || 'Polling failed',
            timestamp: Date.now()
          })
        onUpdateStateRef.current(newConv, null)
        return newConv
      })
      setTaskId(null)
    } finally {
      pollingRef.current = false
      setIsGenerating(false)
    }
  }, [])

  // Resume polling on mount if taskId exists
  useEffect(() => {
    if (taskId && !pollingRef.current) {
      pollTask(taskId)
    }
  }, [taskId, pollTask])

  const handleSend = async prompt => {
    if (!prompt.trim()) {
      message.error('Please enter a prompt')
      return
    }
    if (!selectedReference) {
      message.warning('Please select a reference video')
      return
    }

    const newUserMsg = {
      type: 'user',
      content: prompt,
      timestamp: Date.now(),
      reference: selectedReference
    }

    const loadingMsg = {
      type: 'loading',
      id: 'initial-loading'
    }

    const nextConversation = [...conversation, newUserMsg, loadingMsg]
    setConversation(nextConversation)
    setIsGenerating(true)

    try {
      const response = await editVideo({
        prompt: prompt,
        video_url: selectedReference,
        video_id: videoId
      })

      const newTaskId = response?.data?.task_id
      if (!newTaskId) throw new Error('Failed to start video generation')

      setTaskId(newTaskId)
      onUpdateStateRef.current(nextConversation, newTaskId)
      // pollTask will be triggered by useEffect
    } catch (error) {
      message.error(error.message || 'Failed to start video edit')
      setConversation(prev => {
        const filtered = prev.filter(m => m.id !== 'initial-loading')
        return [
          ...filtered,
          {
            type: 'error',
            content: error.message || 'Video edit failed',
            timestamp: Date.now()
          }
        ]
      })
      setIsGenerating(false)
    }
  }

  return {
    conversation,
    isGenerating,
    taskId,
    selectedReference,
    setSelectedReference,
    handleSend
  }
}
